export const DEFAULT_GAMEPIX_SID = '77227';
export const GAMEPIX_SID =
  process.env.REACT_APP_GAMEPIX_SID?.trim() || DEFAULT_GAMEPIX_SID;

const GAMEPIX_FEED_URL = 'https://feeds.gamepix.com/v2/json';
const GAMEPIX_PLAY_URL = 'https://play.gamepix.com';
const GAMEPIX_NAMESPACE_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/i;

export interface GamePixGame {
  id: string;
  title: string;
  namespace: string;
  description?: string;
  category?: string;
  orientation?: 'all' | 'landscape' | 'portrait' | string;
  quality_score?: number;
  width?: number;
  height?: number;
  date_modified?: string;
  date_published?: string;
  banner_image: string;
  image?: string;
  url: string;
}

export interface GamePixFeed {
  version?: string;
  title?: string;
  feed_url?: string;
  next_url?: string;
  previous_url?: string;
  first_page_url?: string;
  last_page_url?: string;
  modified?: string;
  items: GamePixGame[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const isGamePixGame = (value: unknown): value is GamePixGame => {
  if (!isRecord(value)) return false;

  return (
    typeof value.id === 'string' &&
    typeof value.title === 'string' &&
    typeof value.namespace === 'string' &&
    GAMEPIX_NAMESPACE_PATTERN.test(value.namespace) &&
    typeof value.banner_image === 'string' &&
    typeof value.url === 'string'
  );
};

export const parseGamePixFeed = (payload: unknown): GamePixFeed => {
  if (!isRecord(payload)) {
    throw new Error('O feed do GamePix retornou um formato inválido.');
  }

  const items = Array.isArray(payload.items)
    ? payload.items.filter(isGamePixGame)
    : [];

  return {
    version: typeof payload.version === 'string' ? payload.version : undefined,
    title: typeof payload.title === 'string' ? payload.title : undefined,
    feed_url:
      typeof payload.feed_url === 'string' ? payload.feed_url : undefined,
    next_url:
      typeof payload.next_url === 'string' ? payload.next_url : undefined,
    previous_url:
      typeof payload.previous_url === 'string'
        ? payload.previous_url
        : undefined,
    first_page_url:
      typeof payload.first_page_url === 'string'
        ? payload.first_page_url
        : undefined,
    last_page_url:
      typeof payload.last_page_url === 'string'
        ? payload.last_page_url
        : undefined,
    modified:
      typeof payload.modified === 'string' ? payload.modified : undefined,
    items,
  };
};

export const buildGamePixFeedUrl = (page = 1, pagination = 12) => {
  const params = new URLSearchParams({
    sid: GAMEPIX_SID,
    pagination: String(Math.max(1, pagination)),
    page: String(Math.max(1, page)),
    order: 'quality',
  });

  return `${GAMEPIX_FEED_URL}?${params.toString()}`;
};

export const normalizeGamePixNamespace = (value?: string | null) => {
  if (!value) return null;

  let decoded = value;
  try {
    decoded = decodeURIComponent(value);
  } catch {
    return null;
  }

  const namespace = decoded.trim().toLowerCase();
  return GAMEPIX_NAMESPACE_PATTERN.test(namespace) ? namespace : null;
};

export const buildGamePixGameUrl = (namespace: string) => {
  const normalizedNamespace = normalizeGamePixNamespace(namespace);
  if (!normalizedNamespace) return null;

  return `${GAMEPIX_PLAY_URL}/${normalizedNamespace}/embed?sid=${encodeURIComponent(
    GAMEPIX_SID,
  )}`;
};

export const formatGamePixTitle = (namespace: string) =>
  namespace
    .split('-')
    .filter(Boolean)
    .map(word =>
      /^\d+d$/i.test(word)
        ? word.toUpperCase()
        : word.charAt(0).toUpperCase() + word.slice(1),
    )
    .join(' ');

export async function fetchGamePixGames(
  page = 1,
  signal?: AbortSignal,
): Promise<GamePixFeed> {
  const response = await fetch(buildGamePixFeedUrl(page), {
    headers: {
      Accept: 'application/feed+json, application/json',
    },
    signal,
  });

  if (!response.ok) {
    throw new Error(`Não foi possível carregar os jogos (${response.status}).`);
  }

  return parseGamePixFeed(await response.json());
}
